import { useState, useEffect } from "react";
import { X, Plus, Trash2, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { http, absoluteUrl } from "../lib/api";
import { UploadButton } from "../components/UploadButton";
import { CardFraming } from "../components/CardFraming";

const EMPTY = {
  name: "", jersey: "", age: "", position: "", sport: "Football", team: "", grad_year: "",
  height: "", weight: "", hometown: "", photo_url: "", hero_url: "", team_logo_url: "",
  season: "", bio: "",
  card_focus_x: 0.5, card_focus_y: 0.25, card_zoom: 1,
  hero_focus_x: 0.5, hero_focus_y: 0.35, hero_zoom: 1,
  gallery: [], stats: [], achievements: [], videos: [],
  socials: { instagram: "", twitter: "", tiktok: "", youtube: "", hudl: "", website: "" },
  featured: false, published: true,
};

const STAT_TEMPLATES = {
  "wide receiver": ["Rec Yards", "Receptions", "Touchdowns", "40 Yard"],
  "tight end": ["Rec Yards", "Receptions", "Touchdowns", "Bench"],
  "running back": ["Rush Yards", "Carries", "Touchdowns", "Yards/Carry"],
  quarterback: ["Pass Yards", "Touchdowns", "Completion %", "Interceptions"],
  linebacker: ["Tackles", "Sacks", "Interceptions", "Forced Fumbles"],
  "defensive back": ["Tackles", "Interceptions", "Pass Deflections", "40 Yard"],
  "defensive line": ["Tackles", "Sacks", "Tackles For Loss", "Bench"],
  "offensive line": ["Pancakes", "Starts", "Bench", "Squat"],
  kicker: ["FG Made", "Longest FG", "FG %", "Touchbacks"],
};

const POSITION_OPTIONS = [
  "Quarterback", "Running Back", "Wide Receiver", "Tight End", "Offensive Line",
  "Defensive Line", "Linebacker", "Defensive Back", "Kicker",
];

const Section = ({ title, hint, children }) => (
  <div className="border border-white/10 bg-[#0f0f0f] p-6 sm:p-8 space-y-6">
    <div>
      <h2 className="font-display text-3xl">{title}</h2>
      {hint && <p className="text-xs text-zinc-500 mt-1">{hint}</p>}
    </div>
    {children}
  </div>
);

const Field = ({ label, testid, hint, ...props }) => (
  <label className="block">
    <span className="label-mono">{label}</span>
    <input
      data-testid={testid}
      autoComplete="off"
      className="mt-2 w-full bg-[#111111] border-b-2 border-white/10 focus:border-[#FF5A00] outline-none px-4 py-3 text-sm"
      {...props}
    />
    {hint && <span className="block text-xs text-zinc-500 mt-2">{hint}</span>}
  </label>
);

export default function AthleteForm({ athlete, onClose, onSaved }) {
  const draftKey = `fua_draft_${athlete?.athlete_id || "new"}`;
  const [form, setForm] = useState(() => {
    const base = athlete
      ? { ...EMPTY, ...athlete, socials: { ...EMPTY.socials, ...(athlete.socials || {}) } }
      : EMPTY;
    try {
      const saved = JSON.parse(localStorage.getItem(draftKey) || "null");
      return saved ? { ...base, ...saved, socials: { ...base.socials, ...(saved.socials || {}) } } : base;
    } catch {
      return base;
    }
  });
  const [restored] = useState(() => !!localStorage.getItem(draftKey));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (restored) toast.info("Unsaved draft restored");
  }, [restored]);

  useEffect(() => {
    const t = setTimeout(() => {
      const dirty = form.name || form.position || form.photo_url || form.bio;
      if (dirty) localStorage.setItem(draftKey, JSON.stringify(form));
    }, 600);
    return () => clearTimeout(t);
  }, [form, draftKey]);

  const discardDraft = () => localStorage.removeItem(draftKey);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setSocial = (k) => (e) =>
    setForm({ ...form, socials: { ...form.socials, [k]: e.target.value } });

  const templateFor = (pos) => STAT_TEMPLATES[(pos || "").trim().toLowerCase()];

  const setPosition = (e) => {
    const position = e.target.value;
    const tpl = templateFor(position);
    const untouched = form.stats.every((s) => !s.label && !s.value);
    setForm({
      ...form,
      position,
      stats: tpl && untouched ? tpl.map((label) => ({ label, value: "" })) : form.stats,
    });
  };

  const applyTemplate = () => {
    const tpl = templateFor(form.position);
    if (!tpl) return;
    const filled = form.stats.filter((s) => s.value);
    const missing = tpl
      .filter((label) => !filled.some((s) => s.label.toLowerCase() === label.toLowerCase()))
      .map((label) => ({ label, value: "" }));
    setForm({ ...form, stats: [...filled, ...missing] });
    toast.success(`${form.position} stat labels added`);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Name is required");
    setSaving(true);
    const payload = {
      ...form,
      age: form.age === "" || form.age === null ? null : Number(form.age),
      card_focus_x: Number(form.card_focus_x ?? 0.5),
      card_focus_y: Number(form.card_focus_y ?? 0.25),
      card_zoom: Number(form.card_zoom ?? 1),
      hero_focus_x: Number(form.hero_focus_x ?? 0.5),
      hero_focus_y: Number(form.hero_focus_y ?? 0.35),
      hero_zoom: Number(form.hero_zoom ?? 1),
      stats: form.stats.filter((s) => s.label && s.value),
      achievements: form.achievements.filter((a) => a.trim()),
      videos: form.videos.filter((v) => v.url),
      gallery: form.gallery.filter((g) => g.trim()),
    };
    delete payload.athlete_id;
    delete payload.slug;
    delete payload.created_at;
    delete payload.updated_at;
    try {
      if (athlete) await http.put(`/athletes/${athlete.athlete_id}`, payload);
      else await http.post("/athletes", payload);
      toast.success(athlete ? "Athlete updated" : "Athlete added");
      discardDraft();
      onSaved();
    } catch (err) {
      if (err.sessionExpired) {
        toast.error("Your session expired — sign in again. Your draft is saved.");
        setTimeout(() => window.location.replace("/login"), 1500);
      } else {
        toast.error(err.response?.data?.detail || "Save failed");
      }
    } finally {
      setSaving(false);
    }
  };

  const listEditor = (key, fields, addLabel) => (
    <div className="space-y-3">
      {form[key].map((item, i) => (
        <div key={i} className="flex flex-col sm:flex-row gap-3 items-stretch">
          {fields.map((f) => (
            <input
              key={f.key}
              data-testid={`${key}-${f.key}-${i}`}
              placeholder={f.placeholder}
              value={typeof item === "string" ? item : item[f.key] || ""}
              onChange={(e) => {
                const next = [...form[key]];
                next[i] = typeof item === "string" ? e.target.value : { ...item, [f.key]: e.target.value };
                setForm({ ...form, [key]: next });
              }}
              className="flex-1 bg-[#111111] border-b-2 border-white/10 focus:border-[#FF5A00] outline-none px-4 py-3 text-sm"
            />
          ))}
          <button
            type="button"
            data-testid={`${key}-remove-${i}`}
            onClick={() => setForm({ ...form, [key]: form[key].filter((_, x) => x !== i) })}
            className="p-3 border border-white/20 hover:border-red-500 hover:text-red-500 shrink-0"
          >
            <Trash2 size={16} />
          </button>
        </div>
      ))}
      <button
        type="button"
        data-testid={`${key}-add-btn`}
        onClick={() =>
          setForm({
            ...form,
            [key]: [
              ...form[key],
              fields.length === 1 && fields[0].key === "value_only"
                ? ""
                : Object.fromEntries(fields.map((f) => [f.key, ""])),
            ],
          })
        }
        className="label-mono flex items-center gap-2 text-[#FF5A00] hover:text-white"
      >
        <Plus size={14} /> {addLabel}
      </button>
    </div>
  );



  return (
    <div className="min-h-screen" data-testid="athlete-form-page">
      <div className="sticky top-0 z-40 backdrop-blur-2xl bg-[#0A0A0A]/85 border-b border-white/10">
        <div className="max-w-5xl mx-auto px-5 sm:px-10 py-4 sm:py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <p className="label-mono text-[#FF5A00]">
              {athlete ? "Editing" : "New Profile"}
              {restored && " · draft restored"}
            </p>
            <h1 className="font-display text-3xl sm:text-4xl leading-none">
              {athlete ? athlete.name : "Add Athlete"}
            </h1>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
            {restored && (
              <button
                type="button"
                data-testid="discard-draft-btn"
                onClick={() => {
                  discardDraft();
                  toast.success("Draft discarded");
                  onClose();
                }}
                className="border border-white/20 hover:border-red-500 hover:text-red-500 px-4 py-3 text-[10px] font-bold uppercase tracking-[0.15em]"
              >
                Discard draft
              </button>
            )}
            <button
              data-testid="form-cancel-btn"
              onClick={onClose}
              className="p-3 border border-white/20 hover:border-white"
            >
              <X size={18} />
            </button>
            <button
              data-testid="form-save-btn"
              onClick={submit}
              disabled={saving}
              className="clip-slant bg-[#FF5A00] hover:bg-[#FF7B33] disabled:opacity-50 px-6 sm:px-7 py-3 sm:py-4 text-xs font-bold uppercase tracking-[0.18em]"
            >
              {saving ? "Saving…" : "Save Athlete"}
            </button>
          </div>
        </div>
      </div>

      <form onSubmit={submit} className="max-w-5xl mx-auto px-5 sm:px-10 py-10 space-y-8">
        <Section title="Identity">
          <div className="grid sm:grid-cols-2 gap-6">
            <Field label="Full Name *" testid="form-name" value={form.name} onChange={set("name")} />
            <Field label="Jersey Number" testid="form-jersey" value={form.jersey || ""} onChange={set("jersey")} placeholder="66" />
              <label className="block">
              <span className="label-mono">Position / Event</span>
              <input
                data-testid="form-position"
                list="position-options"
                autoComplete="off"
                value={form.position || ""}
                onChange={setPosition}
                placeholder="e.g. Wide Receiver"
                className="mt-2 w-full bg-[#111111] border-b-2 border-white/10 focus:border-[#FF5A00] outline-none px-4 py-3 text-sm"
              />
              <datalist id="position-options">
                {POSITION_OPTIONS.map((p) => (
                  <option key={p} value={p} />
                ))}
              </datalist>
            </label>
            <Field label="Sport" testid="form-sport" value={form.sport || ""} onChange={set("sport")} />
            <Field label="Team / Club" testid="form-team" value={form.team || ""} onChange={set("team")} />
            <Field label="Age" testid="form-age" type="number" value={form.age ?? ""} onChange={set("age")} />
            <Field label="Class / Grad Year" testid="form-grad-year" value={form.grad_year || ""} onChange={set("grad_year")} placeholder="2027" />
            <Field label="Height" testid="form-height" value={form.height || ""} onChange={set("height")} placeholder={`6'2"`} />
            <Field
              label="Weight (kg)"
              testid="form-weight"
              value={form.weight || ""}
              onChange={set("weight")}
              placeholder="88"
              hint="Always saved and shown as kg — type lbs and we convert it."
            />
            <Field label="Hometown" testid="form-hometown" value={form.hometown || ""} onChange={set("hometown")} />
            <Field label="Season (on story cards)" testid="form-season" value={form.season || ""} onChange={set("season")} placeholder="2026 Season" />
          </div>
        </Section>

        <Section title="Media">
          <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
            <div className="flex-1">
              <Field label="Profile Photo" testid="form-photo-url" value={form.photo_url || ""} onChange={set("photo_url")} placeholder="Upload or paste https://…" />
            </div>
            <UploadButton
              testid="upload-photo-btn"
              label="Upload photo"
              onUploaded={([url]) => setForm({ ...form, photo_url: url })}
            />
          </div>
          {form.photo_url && (
            <img src={absoluteUrl(form.photo_url)} alt="" className="w-28 h-36 object-cover border border-white/10" />
          )}

          <div>
            <p className="label-mono mb-3">Story Card Framing</p>
            <p className="text-xs text-zinc-500 mb-3">
              Story cards use the profile photo above. Nudge it so the face is never cut off on the 9:16 card.
            </p>
            <CardFraming
              variant="story"
              testidPrefix="framing"
              src={form.photo_url}
              keys={{ x: "card_focus_x", y: "card_focus_y", zoom: "card_zoom" }}
              defaults={{ x: 0.5, y: 0.25, zoom: 1 }}
              focusX={Number(form.card_focus_x ?? 0.5)}
              focusY={Number(form.card_focus_y ?? 0.25)}
              zoom={Number(form.card_zoom ?? 1)}
              onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
            <div className="flex-1">
              <Field label="Hero / Action Photo" testid="form-hero-url" value={form.hero_url || ""} onChange={set("hero_url")} placeholder="Upload or paste https://…" />
            </div>
            <UploadButton
              testid="upload-hero-btn"
              label="Upload hero"
              onUploaded={([url]) => setForm({ ...form, hero_url: url })}
            />
          </div>

          <div>
            <p className="label-mono mb-3">Hero Photo Framing</p>
            <p className="text-xs text-zinc-500 mb-3">
              Controls how the hero photo sits in the big banner on the public profile page.
            </p>
            <CardFraming
              variant="hero"
              testidPrefix="hero-framing"
              src={form.hero_url || form.photo_url}
              keys={{ x: "hero_focus_x", y: "hero_focus_y", zoom: "hero_zoom" }}
              defaults={{ x: 0.5, y: 0.35, zoom: 1 }}
              focusX={Number(form.hero_focus_x ?? 0.5)}
              focusY={Number(form.hero_focus_y ?? 0.35)}
              zoom={Number(form.hero_zoom ?? 1)}
              onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
            <div className="flex-1">
              <Field label="Team Badge / Club Logo" testid="form-team-logo-url" value={form.team_logo_url || ""} onChange={set("team_logo_url")} placeholder="Upload or paste https://…" />
            </div>
            <UploadButton
              testid="upload-team-logo-btn"
              label="Upload badge"
              onUploaded={([url]) => setForm({ ...form, team_logo_url: url })}
            />
          </div>
          {form.team_logo_url && (
            <img
              src={absoluteUrl(form.team_logo_url)}
              alt=""
              className="w-20 h-20 object-contain border border-white/10 bg-[#061224]"
            />
          )}


          <div>
            <div className="flex items-center justify-between gap-3 mb-3">
              <p className="label-mono">Gallery / Downloadable Photos</p>
              <UploadButton
                testid="upload-gallery-btn"
                label="Upload photos"
                multiple
                onUploaded={(urls) => setForm({ ...form, gallery: [...form.gallery, ...urls] })}
              />
            </div>
            {listEditor("gallery", [{ key: "value_only", placeholder: "https://image-url" }], "Add photo URL")}
          </div>

          <div>
            <div className="flex items-center justify-between gap-3 mb-3">
              <p className="label-mono">Highlight Videos (upload MP4 or paste YouTube/Vimeo)</p>
              <UploadButton
                testid="upload-video-btn"
                label="Upload video"
                accept="video/*"
                onUploaded={([url]) =>
                  setForm({ ...form, videos: [...form.videos, { title: "", url }] })
                }
              />
            </div>
            {listEditor(
              "videos",
              [
                { key: "title", placeholder: "Title (e.g. 2026 Junior Season)" },
                { key: "url", placeholder: "https://youtube.com/watch?v=…" },
              ],
              "Add highlight video"
            )}
          </div>
        </Section>

        <Section title="Performance">
          <div>
            <div className="flex items-center justify-between gap-3 mb-3">
              <p className="label-mono">Key Stats</p>
              {templateFor(form.position) && (
                <button
                  type="button"
                  data-testid="apply-stat-template-btn"
                  onClick={applyTemplate}
                  className="border border-[#FF5A00] text-[#FF5A00] hover:bg-[#FF5A00] hover:text-white transition-colors px-4 py-2 text-[10px] font-bold uppercase tracking-[0.15em] flex items-center gap-2"
                >
                  <Wand2 size={13} /> Use {form.position} template
                </button>
              )}
            </div>
            {listEditor(
              "stats",
              [
                { key: "label", placeholder: "Stat label (e.g. Rec Yards)" },
                { key: "value", placeholder: "Value (e.g. 1,204)" },
              ],
              "Add stat"
            )}
          </div>
          <div>
            <p className="label-mono mb-3">Achievements</p>
            {listEditor("achievements", [{ key: "value_only", placeholder: "e.g. All-State First Team" }], "Add achievement")}
          </div>
          <label className="block">
            <span className="label-mono">Bio</span>
            <textarea
              data-testid="form-bio"
              rows={6}
              value={form.bio || ""}
              onChange={set("bio")}
              className="mt-2 w-full bg-[#111111] border-b-2 border-white/10 focus:border-[#FF5A00] outline-none px-4 py-3 text-sm"
            />
          </label>
        </Section>

        <Section title="Social Links">
          <div className="grid sm:grid-cols-2 gap-6">
            {["instagram", "twitter", "tiktok", "youtube", "hudl", "website"].map((k) => (
              <Field
                key={k}
                label={k}
                testid={`form-social-${k}`}
                value={form.socials[k] || ""}
                onChange={setSocial(k)}
                placeholder="https://…"
              />
            ))}
          </div>
        </Section>

        <Section title="Visibility">
          <div className="flex flex-wrap gap-6">
            {[
              ["published", "Published on public roster"],
              ["featured", "Featured athlete"],
            ].map(([k, label]) => (
              <label key={k} className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid={`form-${k}-checkbox`}
                  checked={!!form[k]}
                  onChange={(e) => setForm({ ...form, [k]: e.target.checked })}
                  className="w-5 h-5 accent-[#FF5A00]"
                />
                <span className="text-sm">{label}</span>
              </label>
            ))}
          </div>
        </Section>
      </form>
    </div>
  );
}
